import React, { useCallback, useEffect, useRef } from "react";

declare global {
  interface Window {
    google?: any;
  }
}

/** Centro por defecto: CABA (Buenos Aires). Fallback si no hay coords ni geolocalización. */
const DEFAULT_CENTER = { lat: -34.6037, lng: -58.3816 };

// El picker vive dentro de un Dialog de PrimeReact (z-index alto); las sugerencias
// de Places (.pac-container) se montan en el body con z-index bajo y quedan tapadas
// por el modal. Elevamos su z-index una sola vez para que sean visibles/clickeables.
const ensurePacContainerOnTop = () => {
  if (typeof document === "undefined" || document.getElementById("gmaps-pac-zindex")) {
    return;
  }
  const style = document.createElement("style");
  style.id = "gmaps-pac-zindex";
  style.textContent = ".pac-container{z-index:100000 !important;}";
  document.head.appendChild(style);
};

let googleMapsPromise: Promise<void> | null = null;

/** Inyección única del script de Google Maps (mismo approach que VenueForm). */
const loadGoogleMaps = (apiKey: string): Promise<void> => {
  if (window.google?.maps) return Promise.resolve();
  if (!googleMapsPromise) {
    googleMapsPromise = new Promise<void>((resolve) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        apiKey
      )}&libraries=marker,places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
  return googleMapsPromise;
};

const toCoordNumber = (value: unknown): number =>
  typeof value === "function" ? (value as () => number)() : Number(value);

export interface BenefitLocationMapPickerChange {
  latitude: string;
  longitude: string;
  address?: string;
}

interface BenefitLocationMapPickerProps {
  latitude: string;
  longitude: string;
  /**
   * Input de dirección del formulario padre: el picker le engancha
   * Places Autocomplete (completa dirección + coordenadas).
   */
  addressInputRef?: React.RefObject<HTMLInputElement>;
  onPick: (change: BenefitLocationMapPickerChange) => void;
}

export const BenefitLocationMapPicker = ({
  latitude,
  longitude,
  addressInputRef,
  onPick,
}: BenefitLocationMapPickerProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  // Refs para leer siempre la última versión sin re-inicializar el mapa.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const coordsRef = useRef({ latitude, longitude });
  coordsRef.current = { latitude, longitude };

  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps || googleMapRef.current) return;

    const lat = parseFloat(coordsRef.current.latitude);
    const lng = parseFloat(coordsRef.current.longitude);
    const hasCoords = !isNaN(lat) && !isNaN(lng);
    const center = hasCoords ? { lat, lng } : { ...DEFAULT_CENTER };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 14,
      mapId: "benefit-location-map",
    });

    googleMapRef.current = map;

    // Sin coordenadas previas: centrar la vista en la ubicación actual del admin
    // (o quedará en CABA si la deniega/no está disponible). Solo mueve la vista y el
    // marcador; las coordenadas reales se setean cuando busca, hace click o arrastra.
    if (!hasCoords && typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          map.setCenter(here);
          if (markerRef.current) {
            try {
              markerRef.current.position = here;
            } catch (_) {}
          }
        },
        () => undefined,
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 600000 }
      );
    }

    // AdvancedMarkerElement arrastrable (si la librería marker está disponible).
    if (window.google.maps.marker?.AdvancedMarkerElement) {
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: center,
        gmpDraggable: true,
        title: "Ubicación del partner",
      });

      marker.addListener("dragend", () => {
        const pos = marker.position;
        if (!pos) return;
        const newLat = toCoordNumber(pos.lat);
        const newLng = toCoordNumber(pos.lng);
        if (isNaN(newLat) || isNaN(newLng)) return;
        onPickRef.current({
          latitude: newLat.toFixed(6),
          longitude: newLng.toFixed(6),
        });
      });

      markerRef.current = marker;
    }

    // Click en el mapa para posicionar el marcador.
    map.addListener("click", (e: any) => {
      const latLng = e.latLng;
      if (!latLng) return;
      const newLat = latLng.lat();
      const newLng = latLng.lng();
      onPickRef.current({
        latitude: newLat.toFixed(6),
        longitude: newLng.toFixed(6),
      });

      if (markerRef.current) {
        try {
          markerRef.current.position = { lat: newLat, lng: newLng };
        } catch (_) {}
      }
    });

    // Places Autocomplete sobre el input de dirección del formulario.
    if (addressInputRef?.current && window.google.maps.places) {
      // Asegura que la lista de sugerencias quede por encima del Dialog.
      ensurePacContainerOnTop();

      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { fields: ["formatted_address", "geometry"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry?.location) return;

        const newLat = place.geometry.location.lat();
        const newLng = place.geometry.location.lng();

        onPickRef.current({
          latitude: newLat.toFixed(6),
          longitude: newLng.toFixed(6),
          address: place.formatted_address || undefined,
        });

        map.setCenter({ lat: newLat, lng: newLng });
        map.setZoom(17);

        if (markerRef.current) {
          try {
            markerRef.current.position = { lat: newLat, lng: newLng };
          } catch (_) {}
        }
      });
    }
  }, [addressInputRef]);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    let cancelled = false;
    loadGoogleMaps(apiKey).then(() => {
      if (!cancelled) initMap();
    });

    return () => {
      cancelled = true;
    };
  }, [initMap]);

  // Sincroniza el marcador cuando lat/lng cambian desde afuera.
  useEffect(() => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (!isNaN(lat) && !isNaN(lng) && markerRef.current && googleMapRef.current) {
      try {
        markerRef.current.position = { lat, lng };
        googleMapRef.current.panTo({ lat, lng });
      } catch (_) {}
    }
  }, [latitude, longitude]);

  return (
    <div
      ref={mapRef}
      style={{
        width: "100%",
        height: "320px",
        borderRadius: "8px",
        border: "1px solid #dee2e6",
      }}
    >
      {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
        <div className="flex align-items-center justify-content-center h-full text-color-secondary">
          <div className="text-center">
            <i className="pi pi-map-marker text-4xl mb-2" />
            <p>Configure REACT_APP_GOOGLE_MAPS_API_KEY para habilitar el mapa</p>
            <p className="text-sm">Puede ingresar la dirección manualmente arriba</p>
          </div>
        </div>
      )}
    </div>
  );
};
